import React, { useState,  useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  InputAdornment,
  FormControl,
  OutlinedInput,
  Theme,
  useMediaQuery,
  Grid,
} from '@mui/material';
import { emailTemplate } from '../templates/emailTemplate';
import { useUser } from '../contexts/UserContext';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import { fetchApi } from '../services/api';
import { ApiTableResponse1 } from '../types/print';

interface Company {
  거래처번호: string;
  거래처명: string;
  대표이메일: string;
}

interface Customer {
  고객번호: string;
  고객명: string;
  이메일: string;
}

interface ApiResponse {
  table: Company[];
}
interface ApiResponse1 {
  table: Customer[];
}

interface SmtpConfig {
  host: string;
  port: number;
  secure: boolean;
  auth: {
    user: string;
    pass: string;
  };
}

interface SmtpInfo {
  서버주소: string;
  포트: number;
  메일주소: string;
  비밀번호: string;
  보안연결: boolean;
  서버업체: string;
}

interface SmtpResponse {
  table: SmtpInfo[];
}

interface EmailRequestData {
  recipients: string[];
  subject: string;
  body: string;
  smtp_server?: string;
  smtp_port?: number;
  smtp_username?: string;
  smtp_password?: string;
}

interface EmailShareDialogProps {
  open: boolean;
  onClose: () => void;
  selectedItinerary: {
    일정번호: number;
  } | null;
  onSuccess: () => void;
  onError: (error: string) => void;
}

const EmailShareDialog: React.FC<EmailShareDialogProps> = ({
  open,
  onClose,
  selectedItinerary,
  onSuccess,
  onError
}) => {
  const { userInfo, companyInfo } = useUser();
  const [emailList, setEmailList] = useState('');
  const [messageSubject, setMessageSubject] = useState('');
  const [messageOption, setMessageOption] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);
  const [customerSearchKeyword, setCustomerSearchKeyword] = useState('');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [smtpInfo, setSmtpInfo] = useState<SmtpInfo | null>(null);

  // 거래처 목록 로드
  const loadCompanies = async () => {
    try {
      const CompanyNo = localStorage.getItem('CompanyNo');
      const response = await fetchApi<ApiResponse>('ITN_S_간편일정거래처', [
        { 컬럼명: '@업체번호', 값: CompanyNo }
      ]);
      setCompanies(response.data.table);
    } catch (error) {
      console.error('거래처 목록 로드 실패:', error);
    }
  };

  // 고객 목록 로드 함수
  const loadCustomers = async () => {
    try {
      const CompanyNo = localStorage.getItem('CompanyNo');
      const response = await fetchApi<ApiResponse1>('ITN_S_간편일정고객조회', [
        { 컬럼명: '@업체번호', 값: CompanyNo }
      ]);
      if (response.data?.table) {
        setCustomers(response.data?.table);
      }
    } catch (error) {
      console.error('고객 목록 로드 실패:', error);
    }
  };

  // SMTP 정보 로드 함수
  const loadSmtpInfo = async () => {
    try {
      const CompanyNo = localStorage.getItem('CompanyNo');
      const response = await fetchApi<SmtpResponse>('WSP_S_회사SMTP정보', [
        { 컬럼명: '@업체번호', 값: CompanyNo }
      ]);

      if (response.data.table?.[0]) {
        setSmtpInfo(response.data.table[0]);
      }
    } catch (error) {
      console.error('SMTP 정보 로드 실패:', error);
    }
  };

  // 컴포넌트 마운트 시 SMTP 정보 로드
  useEffect(() => {
    if (open) {
      loadSmtpInfo();
    }
  }, [open]);

  const handleSend = async () => {
    if (!selectedItinerary) return;
    
    setIsSending(true);
    try {
      const recipients = emailList.split(',').map(email => email.trim());

      const emailPromises = recipients.map(async (email) => {
        const matchingCompany = selectedCompanies.find(company => company.대표이메일 === email);
        const companyUrl = matchingCompany 
          ? `${window.location.origin}/preview/${selectedItinerary.일정번호}?companyId=${matchingCompany.거래처번호}`
          : `${window.location.origin}/preview/${selectedItinerary.일정번호}`;

        const formattedMessage = messageOption.replace(/\n/g, '<br>');
        const mailBody = emailTemplate
          .replace('##메세지', formattedMessage)
          .replaceAll('#여행사명', userInfo?.업체명 || '랜드박스')
          .replace('##업체명', companyInfo?.업체명 || '랜드박스')
          .replace('##여행사주소', companyInfo?.기본주소  + ' ' + companyInfo?.상세주소 || '서울시 강남구')
          .replace('##대표자명', companyInfo?.대표자 || '홍길동')
          .replace('##대표이메일', companyInfo?.이메일 || 'support@landbox.com')
          .replace('#운영시간', '평일 09:00-18:00')
          .replace('##발송년도', new Date().getFullYear().toString())
          .replace('#url', companyUrl)
          .replace('#logo', companyInfo?.회사로고 || '홍길동');

      // 서버 EmailRequest 클래스 형식에 맞게 구조 변경
      let postData: EmailRequestData = {
        recipients: [email],
        subject: messageSubject,
        body: mailBody,
      };

      // SMTP 정보가 모두 있는 경우에만 SMTP 설정 추가
      if (smtpInfo?.서버주소 && 
          smtpInfo?.포트 && 
          smtpInfo?.메일주소 && 
          smtpInfo?.비밀번호) {
        postData = {
          ...postData,
          smtp_server: smtpInfo.서버주소,
          smtp_port: smtpInfo.포트,
          smtp_username: smtpInfo.메일주소,
          smtp_password: smtpInfo.비밀번호
        };
      }

      const response = await fetch('https://revato.mytripbox.co.kr/itinerary/send-email', {
        // const response = await fetch('http://127.0.0.1:9001/itinerary/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(postData)
      });

      if (!response.ok) {
        throw new Error(`Email sending failed: ${response.status}`);
      }
    });

    await Promise.all(emailPromises);
    onSuccess();
    handleClose();
  } catch (error) {
    onError('이메일 전송에 실패했습니다. 설정을 확인해주세요.');
  } finally {
    setIsSending(false);
  }
};

  const handleClose = () => {
    setEmailList('');
    setMessageSubject('');
    setMessageOption('');
    onClose();
  };

  const handleCompanySelect = (company: Company) => {
    setSelectedCompanies(prev => {
      const isSelected = prev.some(c => c.거래처번호 === company.거래처번호);
      if (isSelected) {
        return prev.filter(c => c.거래처번호 !== company.거래처번호);
      }
      return [...prev, company];
    });
  };

  const handleSelectAll = () => {
    setSelectedCompanies(companies);
  };

  const handleDeselectAll = () => {
    setSelectedCompanies([]);
  };

  const handleApplyCompanies = () => {
    if (selectedCompanies.length > 0) {
      // 이메일이 있는 거래처만 필터링하고 사용자에게 알림
      const validCompanies = selectedCompanies.filter(company => company.대표이메일);
      if (validCompanies.length === 0) {
        onError('선택한 거래처 중 이메일 주소가 등록된 거래처가 없습니다.');
        return;
      }

      const emails = validCompanies.map(company => company.대표이메일).join(', ');
      setEmailList(emails);
      
      // 제목이 비어있는 경우 기본값 설정
      if (!messageSubject) {
        setMessageSubject(`[${companyInfo?.업체명 || ''}] 일정표 전달드립니다.`);
      }
      
      setCompanyDialogOpen(false);
    }
  };

  const filteredCompanies = useMemo(() => 
    companies.filter(company => company.거래처명.includes(searchKeyword)),
    [companies, searchKeyword]
  );

  // 고객 선택 핸들러
  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomers(prev => {
      const isSelected = prev.some(c => c.고객번호 === customer.고객번호);
      if (isSelected) {
        return prev.filter(c => c.고객번호 !== customer.고객번호);
      }
      return [...prev, customer];
    });
  };

  // 고객 전체 선택/해제 핸들러
  const handleSelectAllCustomers = () => {
    setSelectedCustomers(customers);
  };

  const handleDeselectAllCustomers = () => {
    setSelectedCustomers([]);
  };

  // 선택된 고객 적용
  const handleApplyCustomers = () => {
    if (selectedCustomers.length > 0) {
      const validCustomers = selectedCustomers.filter(customer => customer.이메일);
      if (validCustomers.length === 0) {
        onError('선택한 고객 중 이메일 주소가 등록된 고객이 없습니다.');
        return;
      }

      const emails = validCustomers.map(customer => customer.이메일).join(', ');
      setEmailList(emails);
      
      if (!messageSubject) {
        setMessageSubject(`[${companyInfo?.업체명 || ''}] 일정표 전달드립니다.`);
      }
      
      setCustomerDialogOpen(false);
    }
  };

  // 필터링된 고객 목록
  const filteredCustomers = useMemo(() => 
    customers.filter(customer => customer.고객명.includes(customerSearchKeyword)),
    [customers, customerSearchKeyword]
  );

  const CompanySelectDialog = useMemo(() => (
    <Dialog 
      open={companyDialogOpen} 
      onClose={() => setCompanyDialogOpen(false)} 
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      TransitionProps={{
        timeout: 0
      }}
      PaperProps={{
        style: {
          height: '80vh',
          overflow: 'auto',
          maxWidth: 'sm',
          width: '100%',
          margin: '0 auto',
          // borderRadius: '10px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white',
          py: 2,
        }}
      >
        <Typography variant="h6">거래처 선택</Typography>
        <Box>
          <Button
            onClick={handleApplyCompanies}
            sx={{ 
              color: 'white',
              mr: 1,
              height: '42px',
              width: '100px',
              border: '1px solid rgba(255, 255, 255, 0.5)',
              '&:hover': {
                border: '1px solid #fff',
                backgroundColor: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            적용
          </Button>
          <IconButton
            onClick={() => setCompanyDialogOpen(false)}
            sx={{ 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent 
        sx={{ 
          mt: 2,
          height: 'calc(100% - 76px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 2, sm: 3 }
        }}
      >
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl variant="outlined" size="small" sx={{ flexGrow: 1 }}>
            <OutlinedInput
              placeholder="거래처명 검색"
              value={searchKeyword}
              onChange={(e) => {
                e.preventDefault();
                setSearchKeyword(e.target.value);
              }}
              onKeyDown={(e) => e.stopPropagation()}
              autoFocus={false}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              sx={{ height: '36px' }}
            />
          </FormControl>
          <Button 
            variant="outlined" 
            onClick={handleSelectAll}
            sx={{ 
              height: '36px',
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              px: 1.5  // 좌우 패딩 조정
            }}
          >
            전체선택
          </Button>
          <Button 
            variant="outlined" 
            onClick={handleDeselectAll}
            sx={{ 
              height: '36px',
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              px: 1.5  // 좌우 패딩 조정
            }}
          >
            전체해제
          </Button>
        </Box>
        <Box sx={{ 
          flexGrow: 1, 
          overflow: 'auto',
          border: '1px solid #ccc',
          '& .MuiGrid-container': {
            margin: 0,
            width: '100%'
          }
        }}>
          <Grid container spacing={0} sx={{ p: { xs: 0.5, sm: 1 } }}>
            {filteredCompanies.map((company) => (
              <Grid item xs={12} sm={6} md={4} key={company.거래처번호} >
                <ListItem
                  component="div"
                  dense
                  onClick={() => handleCompanySelect(company)}
                  sx={{ 
                    // border: '1px solid #eee', 
                    borderRadius: 1,
                    cursor: 'pointer',
                    mx: { xs: 0, sm: 0 },
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={selectedCompanies.some(c => c.거래처번호 === company.거래처번호)}
                  />
                  <ListItemText primary={company.거래처명} />
                </ListItem>
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  ), [companyDialogOpen, searchKeyword, companies, selectedCompanies]);

  const CustomerSelectDialog = useMemo(() => (
    <Dialog 
      open={customerDialogOpen} 
      onClose={() => setCustomerDialogOpen(false)} 
      maxWidth="sm"
      fullWidth
      TransitionProps={{
        timeout: 0
      }}
      PaperProps={{
        style: {
          height: '80vh',
          overflow: 'auto',
          maxWidth: 'sm',
          width: '100%',
          margin: '0 auto',
          // borderRadius: '10px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white',
          py: 2,
        }}
      >
        <Typography variant="h6">고객 선택</Typography>
        <Box>
          <Button
            onClick={handleApplyCustomers}
            sx={{ 
              color: 'white',
              mr: 1,
              height: '42px',
              width: '100px',
              border: '1px solid rgba(255, 255, 255, 0.5)',
              '&:hover': {
                border: '1px solid #fff',
                backgroundColor: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            적용
          </Button>
          <IconButton
            onClick={() => setCustomerDialogOpen(false)}
            sx={{ 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent 
        sx={{ 
          mt: 2,
          height: 'calc(100% - 76px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 2, sm: 3 }
        }}
      >
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl variant="outlined" size="small" sx={{ flexGrow: 1 }}>
            <OutlinedInput
              placeholder="고객명 검색"
              value={customerSearchKeyword}
              onChange={(e) => {
                e.preventDefault();
                setCustomerSearchKeyword(e.target.value);
              }}
              onKeyDown={(e) => e.stopPropagation()}
              autoFocus={false}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              sx={{ height: '36px' }}
            />
          </FormControl>
          <Button 
            variant="outlined" 
            onClick={handleSelectAllCustomers}
            sx={{ 
              height: '36px',
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              px: 1.5
            }}
          >
            전체선택
          </Button>
          <Button 
            variant="outlined" 
            onClick={handleDeselectAllCustomers}
            sx={{ 
              height: '36px',
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              px: 1.5
            }}
          >
            전체해제
          </Button>
        </Box>
        <Box sx={{ 
          flexGrow: 1, 
          overflow: 'auto',
          border: '1px solid #ccc',
          '& .MuiGrid-container': {
            margin: 0,
            width: '100%'
          }
        }}>
          <Grid container spacing={0} sx={{ p: { xs: 0.5, sm: 1 } }}>
            {filteredCustomers.map((customer) => (
              <Grid item xs={12} sm={6} md={4} key={customer.고객번호}>
                <ListItem
                  component="div"
                  dense
                  onClick={() => handleCustomerSelect(customer)}
                  sx={{ 
                    borderRadius: 1,
                    cursor: 'pointer',
                    mx: { xs: 0, sm: 0 },
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={selectedCustomers.some(c => c.고객번호 === customer.고객번호)}
                  />
                  <ListItemText primary={customer.고객명} />
                </ListItem>
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  ), [customerDialogOpen, customerSearchKeyword, customers, selectedCustomers]);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            width: '100%',
            height: '80vh',
            margin: { xs: 0, sm: 32 },
            maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' },
            borderRadius: { xs: 0, sm: 1 }
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'primary.main',
            color: 'white',
            py: 2,
          }}
        >
          <Typography variant="h6">
            {selectedCompanies.length > 0 ? '일정전송(거래처)' : 
             selectedCustomers.length > 0 ? '일정전송(고객)' : '일정전송'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<BusinessIcon />}
              onClick={() => {
                loadCompanies();
                setCompanyDialogOpen(true);
              }}
              sx={{ 
                color: 'white',
                height: '42px',
                border: '1px solid rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  border: '1px solid #fff',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)'
                }
              }}
            >
              거래처 리스트
            </Button>
            <Button
              startIcon={<PersonIcon />}
              onClick={() => {
                loadCustomers();
                setCustomerDialogOpen(true);
              }}
              sx={{ 
                color: 'white',
                height: '42px',
                border: '1px solid rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  border: '1px solid #fff',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)'
                }
              }}
            >
              고객 리스트
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {selectedCompanies.length > 0 
                ? '선택된 거래처의 이메일 주소'
                : selectedCustomers.length > 0 ? '선택된 고객의 이메일 주소' : '최대 5개 이메일 주소 입력 가능 (쉼표로 구분)'}
            </Typography>
            <TextField
              margin="dense"
              label="받는 사람 이메일"
              type="email"
              fullWidth
              variant="outlined"
              value={emailList}
              onChange={(e) => {
                console.log('이메일 리스트 변경:', e.target.value); // 디버깅용
                setEmailList(e.target.value);
                if (selectedCompanies.length > 0) {
                  setSelectedCompanies([]);
                }
              }}
              disabled={selectedCompanies.length > 0 || selectedCustomers.length > 0}
              helperText={selectedCompanies.length > 0 || selectedCustomers.length > 0 
                ? `선택된 거래처 또는 고객 수: ${selectedCompanies.length + selectedCustomers.length}`
                : '여러 이메일은 쉼표(,)로 구분해주세요'}
            />
            <TextField
              margin="dense"
              label="제목"
              type="text"
              fullWidth
              variant="outlined"
              value={messageSubject}
              onChange={(e) => setMessageSubject(e.target.value)}
              sx={{ mt: 2 }}
            />
            <TextField
              margin="dense"
              label="메시지(선택사항)"
              multiline
              rows={15}
              fullWidth
              variant="outlined"
              value={messageOption}
              onChange={(e) => setMessageOption(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Box>
          <Box sx={{ mt: 2, fontSize: '12px', color: 'primary' }}>
            * 일정표 다운로드와 미리보기 웹주소는 자동으로 첨부됩니다.
            <br />
            * 거래처를 선택하여 전송시 일정표 헤더에 거래처명, 거래처 연락처가 표시됩니다. 
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} sx={{ height: '36px', width: '80px' }}>취소</Button>
          <Button 
            onClick={handleSend} 
            variant="contained" 
            disabled={isSending || !emailList || !messageSubject}
            sx={{ 
              minWidth: '80px',
              height: '36px',
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'white',
                color: 'primary.main',
                border: '1px solid',
                borderColor: 'primary.main'
              }
            }}
          >
            {isSending ? '전송 중...' : '전송'}
          </Button>
        </DialogActions>
      </Dialog>
      {CompanySelectDialog}
      {CustomerSelectDialog}
    </>
  );
};

export default EmailShareDialog;
